import { CommonModule } from '@angular/common';
import { Component, Input, input, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionComponent, ButtonComponent, DatepickerComponent, DropdownSelectComponent, UploadComponent } from '@ppl/ui';
import { NzCardComponent, NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { forkJoin, of, Subscription } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';
import { UserService } from 'src/app/services/user.service';
import { SectionsService } from 'src/app/services/sections.service';
import { ProviderServiceCredentialsService } from 'src/app/services/provider-service-credentials.service';
import { EnrollmentDataService } from 'src/app/services/enrollment-data.service';
import { SharedDataService } from 'src/app/shared/services/shared-data-service';
import { UploadHandlerService } from 'src/app/shared/services/upload-handler.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { NgZorroModule } from 'src/app/shared/modules/ng-zorro/ng-zorro.module';
import { BaseComponent } from 'src/app/shared/utils/base.component';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { DateTimeFormatterService } from 'src/app/shared/utils/date-time-formatter.service';
import { STATIC_DATA } from 'src/app/shared/constants/static-data.constants';
import { ReferralDataService } from 'src/app/services/referral-data.service';
import { AuthorizationUtility } from 'src/app/shared/authorization/auth.utility';
import { MEMBER_VIEW_PAGE_IDS } from 'src/app/shared/constants/page.constants';
import { StorageService } from 'src/app/services/storage.service';

@Component({
  selector: 'ppl-provider-service-credentials',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    NzFormModule,
    NzCardModule,
    NzCheckboxModule,
    DatepickerComponent,
    AccordionComponent,
    ButtonComponent,
    DropdownSelectComponent,
    UploadComponent,
    MessageModule,
    TableModule,
  ],
  templateUrl: './provider-service-credentials.html',
  providers: [ProviderServiceCredentialsService],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
  styleUrl: './provider-service-credentials.css',
})
export class ProviderServiceCredentials extends BaseComponent {
  @Input() mode = 'read';
  @Input() providerType: string = '';
  @Input() showAccordion = true;
  @Input() isSummaryView = false;
  serviceCredentialsForm: any;
  providerAssociations: any[] = [];
  providerServices: any[] = [];
  private _subs: Subscription[] = [];
  programCode: any = '';
  providerId: number = 0;
  participantId: number = 0;
  canView: boolean = false;
  canEdit: boolean = false;
  isReadOnlyView: boolean = false;
  selectedLead: any;
  flow: string = '';
  serviceCredentials: any[] = [];
  allServiceCredentials: any[] = [];
  selectedAssociationCredentials: any[] = [];
  formSubmitRequested = false;
  private pendingFileDeletions: { fileId: any; credential: any }[] = [];
  cols = [
    { field: 'pplParticipantId', header: 'Participant PPL Id', jsonpath: 'pplParticipantId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'participantName', header: 'Participant Name', jsonpath: 'participantName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'code', header: 'Service Code', jsonpath: 'code', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'licenseNumber', header: 'License Number', jsonpath: 'licenseNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'expirationDate', header: 'Expiration Date', jsonpath: 'expirationDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'actions', header: 'Actions', jsonpath: 'actions', isDisplayedAtHeader: 'y', isSearchable: 'n' }
  ];
  constructor(private fb: FormBuilder,
    private userService: UserService,
    private sectionsService: SectionsService,
    private enrollmentDataService: EnrollmentDataService,
    private providerServiceCredentialsService: ProviderServiceCredentialsService,
    private sharedDataService: SharedDataService,
    private uploadHandler: UploadHandlerService,
    private notificationService: NotificationService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private referralDataService: ReferralDataService,
    private authUtility: AuthorizationUtility,
    private storageService: StorageService,
  ) {
    super();
   }

  ngOnInit(): void {
    this.programCode = this.userService.getSelectedProgram()?.program_code;
    this.selectedLead = this.userService.getSelectedLead();
    this.flow = this.sectionsService.getFlow()!;
    // view + edit are independent permissions: edit implies view, view-only opens the
    // popup in readonly mode, neither hides the row action entirely.
    const pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
    const hasViewAccess = pageId ? this.authUtility.isTabVisible(pageId, 'serviceCredentials') : true;
    const hasEditAccess = pageId ? this.authUtility.isTabEditable(pageId, 'serviceCredentials') : true;
    if (this.flow === 'providerEnrollment') {
      this.canView = hasViewAccess;
      this.canEdit = hasEditAccess && !this.isSummaryView;
      this.providerId = this.selectedLead
    }
    else {
      this.canView = hasViewAccess;
      this.canEdit = false;
      this.participantId = this.selectedLead;
    }

    this.getProviderAssoc(this.programCode, this.providerId || this.participantId);

    this.referralDataService.nextClicked.subscribe((val) => {
          // we don't require any validation for this step, so mark it as valid
          if (val === STATIC_DATA.SERVICE_CREDENTIALS) {
            this.referralDataService.setStepValidation({ step: STATIC_DATA.SERVICE_CREDENTIALS, val: true });
          }
        });
  }

  getProviderAssoc(program_code: string, leadId: any) {
    const participantId = this.storageService.getSelfEmployerParticipantId();
    this.enrollmentDataService.getAssociationsDetails(program_code, leadId, false, true, participantId).subscribe((response: any) => {
      const data = response.responsedata;
      // A participant can be linked to the same provider under multiple association
      // types. We still show one dropdown entry per participant, but collect every
      // associationId for that participant so service codes can be fetched for all of
      // them together via the POST endpoint.
      const associations = data?.participantAssociations ?? [];
      const byParticipant = new Map<number, any>();
      associations.forEach((assoc: any) => {
        if (!byParticipant.has(assoc.participantId)) {
          byParticipant.set(assoc.participantId, {
            ...assoc,
            displayName: `${assoc.participantFirstName} ${assoc.participantLastName}`,
            associationIds: [],
          });
        }
        byParticipant.get(assoc.participantId).associationIds.push(assoc.participantProviderAssociationId);
      });
      this.providerAssociations = Array.from(byParticipant.values());

      this.getProviderServiceCredentials();
    });
  }

  getProviderServiceCredentials(onComplete?: () => void) {
    // fetch existing credentials from service
    this.providerServiceCredentialsService.getProviderServiceCredentials(this.programCode, this.providerId).subscribe({
      next: (res) => {
        this.transformServiceCredentials(res?.responsedata || []);
        onComplete?.();
      },
      error: (err) => {
        console.error('Error fetching service credentials', err);
        onComplete?.();
      }
    });
  }

  transformServiceCredentials(data: any[]) {
    // Transform the service credentials data, need to group by providerId and participantId
    const groupedData: any = {};
    data?.forEach((item) => {
      const key = `${item.providerId}-${item.participantId}`;
      if (!groupedData[key]) {
        const participant = this.providerAssociations.find(p => p.participantId === item.participantId);
        groupedData[key] = {
          providerId: item.providerId,
          participantId: item.participantId,
          pplParticipantId: participant?.participantPplId,
          participantName: participant?.participantFirstName,
          credentials: []
        };
      }
      groupedData[key].credentials.push(item);
    });

    // Keep all credentials for form patching (to preserve providerServiceCredentialId)
    const groupedDataArray = Object.values(groupedData);
    this.allServiceCredentials = groupedDataArray;
    // Show every group / credential returned by the API — no file-count filter.
    this.serviceCredentials = groupedDataArray;
  }

  getAssociationServiceCodes(associationIds: number[]) {
    // fetch service codes for all of the participant's associations
    this.providerServiceCredentialsService.getAssociationServiceCodes(this.programCode, associationIds ?? []).subscribe({
      next: (res) => {
        this.providerServices = res?.responsedata || [];
        this.initializeForm();
        this.setExistingCredentials();
      },
      error: (err) => {
        console.error('Error fetching association service codes', err);
      }
    });
  }

  initializeForm() {
    // build the form based on provider services
    const serviceCredentials = this.providerServices.map((ps) =>
      this.fb.group({
        checked: [false],
        providerServiceCredentialId: [null],
        providerId: [this.providerId || null],
        participantId: [this.participantId || null],
        servicecodeDetailId: [ps.servicecodeDetailId],
        serviceTypeName: [ps.serviceDescription],
        licenseNumber: [null, [Validators.maxLength(30)]],
        expirationDate: [''],
        fileUploadDetailIds: [null],
        files: [[]],
      })
    );
    
    this.serviceCredentialsForm = this.fb.group({
      participantId: this.participantId || null,
      providerId: this.providerId || null,
      services: this.fb.array(serviceCredentials)
    });

    // setup listeners for checkbox changes to toggle validators
    this.setupCheckedListeners();

    if (this.isReadOnlyView || !this.canEdit) {
      this.serviceCredentialsForm.disable();
    }
  }

  get serviceCredentialsControls() {
    return this.serviceCredentialsForm.get('services') as FormArray;
  }

  onEditCredentialsClick() {
    this.initializeForm();
    this.mode = 'edit';
  }

  onParticipantChange(event: any) {
    this.participantId = event;
    const participant = this.providerAssociations.find(p => p.participantId === this.participantId);
    this.getAssociationServiceCodes(participant?.associationIds);
  }

  private setupCheckedListeners() {
    // clear any existing subscriptions
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];

    const services = this.serviceCredentialsForm.get('services') as FormArray;
    services.controls.forEach((ctrl, index) => {
      // listen to changes on 'checked' control, if checked add validators to other controls
      const checkedCtrl = ctrl.get('checked');
      if (checkedCtrl) {
        const sub = checkedCtrl.valueChanges.subscribe((val: boolean) => {
          const numberCtrl = ctrl.get('licenseNumber');
          const expCtrl = ctrl.get('expirationDate');
          const fileCtrl = ctrl.get('files');
          if (val) {
            numberCtrl?.clearValidators();
            expCtrl?.clearValidators();
            fileCtrl?.setValidators([Validators.minLength(1)]);
          } else {
            numberCtrl?.clearValidators();
            expCtrl?.clearValidators();
            fileCtrl?.clearValidators();
          }
          numberCtrl?.updateValueAndValidity();
          expCtrl?.updateValueAndValidity();
          fileCtrl?.updateValueAndValidity();
        });
        this._subs.push(sub);
      }
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }

  setExistingCredentials() {
    // fetch existing credentials if any and patch the form
    if (this.allServiceCredentials.length > 0) {
      const services = this.serviceCredentialsForm.get('services') as FormArray;

      const participantCredentials = this.allServiceCredentials.filter(ec => ec.participantId === this.participantId).map(ec => ec.credentials).flat();
      // need to iterate through services and patch values based on existing credentials
      services.controls.forEach((ctrl) => {
        const servicecodeDetailId = ctrl.get('servicecodeDetailId')?.value;
        const matchingCredentials = participantCredentials.filter(ec => ec.servicecodeDetailId === servicecodeDetailId);
        const existingCredential = matchingCredentials.find(ec => ec.files && ec.files.length > 0) || matchingCredentials[0];
        if (existingCredential && existingCredential.providerServiceCredentialId) {
          const hasFiles = existingCredential.files && existingCredential.files.length > 0;
          ctrl.patchValue({
            checked: hasFiles,
            providerServiceCredentialId: existingCredential.providerServiceCredentialId || null,
            licenseNumber: existingCredential.licenseNumber,
            expirationDate: existingCredential.expirationDate ? this.dateTimeFormatterService.stringToDate(existingCredential.expirationDate) : null,
            files: existingCredential.files ? existingCredential.files.map((f: any) => {
              return { id: f.providerServiceCredentialFileuploadDetailId, name: f.actualFileName, type: 'Service Credentials' };
            }) : [],
            fileUploadDetailIds: existingCredential.files ? existingCredential.files.map((f: any) => f.providerServiceCredentialFileuploadDetailId) : []
          });
        }
      });
      // reinitialize listeners after patching
      this.setupCheckedListeners();
    }
  }

  onEditCredentials(rowData: any) {
    this.mode = 'edit';
    this.initializeForm();
    this.participantId = rowData.participantId;
    this.onParticipantChange(this.participantId);
    this.selectedAssociationCredentials = rowData;
    this.setExistingCredentials();
  }

  onViewCredentials(rowData: any) {
    this.isReadOnlyView = true;
    this.onEditCredentials(rowData);
  }

  saveCredentials() {
    this.formSubmitRequested = true;
    const formData = this.serviceCredentialsForm.value;
    const hasCheckedServices = formData.services.some((svc: any) => svc.checked);
    const checkedServicesValid = formData.services.filter((svc: any) => svc.checked).every((svc: any) => svc.files.length > 0);

    // If the user deselected every service, prevent submission and explain why.
    if (!hasCheckedServices) {
      this.notificationService.error(
        'No services selected',
        'At least one record should be selected to update or create.'
      );
      return;
    }

    const credentialsValid = this.serviceCredentialsForm.valid && checkedServicesValid;
    if (credentialsValid) {
      formData.participantId = this.participantId;
      // Deselected services are simply omitted from the payload — no file deletions, no null
      // license/expiration writes. Per-file removals queued via onFileRemoved still run.
      const payload = this.getServiceCredentialsPayload();
      this.providerServiceCredentialsService.saveServiceCredentials(this.programCode, payload).subscribe({
        next: (res) => {
          this.processPendingDeletions().subscribe({
            next: () => {
              this.serviceCredentials = [];
              this.allServiceCredentials = [];
              this.getProviderServiceCredentials(() => {
                this.mode = 'read';
                this.selectedAssociationCredentials = [];
                this.participantId = 0;
                this.formSubmitRequested = false;
              });
            },
            error: () => {
              // Even if deletions fail, still refresh the grid
              this.serviceCredentials = [];
              this.allServiceCredentials = [];
              this.getProviderServiceCredentials(() => {
                this.mode = 'read';
                this.selectedAssociationCredentials = [];
                this.participantId = 0;
                this.formSubmitRequested = false;
              });
            }
          });
        },
        error: (err) => {
          console.error('Error saving service credentials', err);
        }
      });
    }
  }

  getServiceCredentialsPayload() {
    const formValue = this.serviceCredentialsForm.value;
    // Only send records for services that are currently checked. Deselected services are
    // omitted entirely (no null writes, no file deletions on the backend).
    const payload = formValue.services
      .filter((svc: any) => svc.checked)
      .map((svc: any) => ({
        providerServiceCredentialId: svc.providerServiceCredentialId,
        providerId: svc.providerId,
        participantId: svc.participantId,
        servicecodeDetailId: svc.servicecodeDetailId,
        licenseNumber: svc.licenseNumber,
        expirationDate: this.dateTimeFormatterService.formatDateToString(svc.expirationDate),
        fileUploadDetailIds: svc.files.map((f: any) => f.id)
      }));

    return payload;
  }

  // Parent upload handler for ppl-upload — upload to backend via SharedService
  onFileSelected(event: any, credential: any) {
    let selectedFileCredentialControl = credential;
    const file = event?.file as File;
    if (!file) return;
    const type = this.programCode || this.userService.getLeadEntry();
    const flowName = (type === 'authorizations') ? 'auth' : this.sharedDataService.getType(type);

    const formData = new FormData();
    formData.append('file', file as any);

    const requestPayload: any = {
      fileType: file.name.split('.').pop() || '',
      otherType: event.documentType || '',
      program: this.programCode,
      type: flowName,
      ...(flowName === 'auth' ? { authDetailId: event.leadId } : { leadId: event.leadId })
    };

    if ((event.consumerId && event.providerId) || (event.source === 'purchaserequest' && event.consumerId)) {
      Object.assign(requestPayload, {
        consumerId: event.consumerId,
        providerId: event.providerId,
        type: event.source || type
      });
    }

    formData.append('json', new Blob([JSON.stringify(requestPayload)], { type: 'application/json' }));

    this.uploadHandler.uploadFromEvent(event, type).subscribe({
      next: (response: any) => {
        const id = response?.responsedata?.file_upload_tracking_number ?? Date.now();
        selectedFileCredentialControl.controls.files.value.push({ id, name: file.name, size: file.size, type: event.documentType });
        selectedFileCredentialControl.updateValueAndValidity();
      },
      error: () => this.notificationService.error('Error', `${file.name} file upload failed.`)
    });
  }

  onFileRemoved(fileId: any, credential: any) {
    const id = fileId;
    let selectedFileCredentialControl = credential;

    // Only remove from UI; defer actual backend deletion until submit
    this.pendingFileDeletions.push({ fileId: id, credential: selectedFileCredentialControl });
    selectedFileCredentialControl.controls.files.patchValue(
      selectedFileCredentialControl.controls.files.value.filter((f: any) => String(f.id) !== String(id))
    );
    selectedFileCredentialControl.updateValueAndValidity();
  }

  private processPendingDeletions() {
    if (this.pendingFileDeletions.length === 0) {
      return of([]);
    }
    const deleteRequests = this.pendingFileDeletions.map(({ fileId, credential }) => {
      const createType = credential?.value.providerServiceCredentialId ? 'detail' : 'new';
      return this.uploadHandler.deleteFile(this.programCode, fileId, createType, 'servicecredential', true);
    });
    this.pendingFileDeletions = [];
    return forkJoin(deleteRequests);
  }

  onDownloadRequested(file: any) {
    this.uploadHandler.downloadFile(file, 'servicecredential');
  }

  onUploadError(payload: any) {
    this.uploadHandler.handleUploadError(payload);
  }

  cancel() {
    this.pendingFileDeletions = [];
    this.mode = 'read';
    this.selectedAssociationCredentials = [];
    this.participantId = 0;
    this.formSubmitRequested = false;
    this.isReadOnlyView = false;
  }
}
